// Lib
import Handsontable from 'handsontable';
import { Events } from 'handsontable/pluginHooks';
import { useCallback, useEffect } from 'react';
import { useHotTableDispatch } from './store/hotTableStoreHooks';
import { setActiveTable } from './store/hotTableStoreActions';
import rafThrottle from '../../../common/utils/lib/rafThrottle';
import { TableDataVersion } from '../../../common/table/TableTypes';
import { ActiveTableObject, ActiveTableSessionHelpers } from './store/hotTableStoreTypes';

const TRACKED_HOOKS: (keyof Events)[] = [
    'afterChange',
    'afterCreateRow',
    'afterCreateCol',
    'afterRemoveRow',
    'afterRemoveCol',
    'afterRowMove',
    'afterColumnMove',
    'afterColumnResize',
    'afterSelection',
    'afterSelectionEnd',
    'afterDeselect',
];

const noop = () => {};

const NOOP_ACTIVE_TABLE_SESSION_HELPERS: ActiveTableSessionHelpers = {
    markDirty: noop,
    scheduleFlush: noop,
    flushNow: noop,
    isDirty: () => false,
};

const getActiveTableStoreEntry = ({
    elementId,
    tableInstance,
}: {
    elementId: string;
    tableInstance: Handsontable;
}): ActiveTableObject => ({
    elementId,
    instance: tableInstance,
    tableDataVersion: TableDataVersion.V2,
    session: NOOP_ACTIVE_TABLE_SESSION_HELPERS,
});

/**
 * Stores the current active Handsontable instance in the HotTable store & clears it when no longer edited.
 */
export const useSyncActiveTableState = (tableInstance: Handsontable | null, elementId: string, isActive: boolean) => {
    const hotTableDispatch = useHotTableDispatch();
    const dispatchUpdateActiveTable = useCallback(
        (activeTable: ActiveTableObject | null) => hotTableDispatch(setActiveTable(activeTable)),
        [hotTableDispatch],
    );

    useEffect(() => {
        if (!tableInstance) return;
        if (!isActive) return;

        dispatchUpdateActiveTable(getActiveTableStoreEntry({ elementId, tableInstance }));

        const throttledUpdateActiveTable = rafThrottle(() =>
            dispatchUpdateActiveTable(getActiveTableStoreEntry({ elementId, tableInstance })),
        );

        TRACKED_HOOKS.forEach((hook) => tableInstance.addHook(hook, throttledUpdateActiveTable));

        return () => {
            // You shouldn't be able to immediately switch from editing one table to another, or edit
            //  two tables at once, so this should be a safe operation (i.e. won't clobber another active table).
            dispatchUpdateActiveTable(null);

            throttledUpdateActiveTable.cancel();

            TRACKED_HOOKS.forEach((hook) => tableInstance.removeHook(hook, throttledUpdateActiveTable));
        };
    }, [dispatchUpdateActiveTable, elementId, isActive, tableInstance]);
};
